import {
  endAt,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  startAt,
  type Query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
} from 'firebase/firestore';
import { geohashQueryBounds } from 'geofire-common';

/**
 * One geohash range to paginate. `start`/`end` are the inclusive string
 * bounds from `geohashQueryBounds`; an empty object paginates the whole
 * collection (used when no location/distance filter applies).
 */
export interface GeoBound {
  start?: string;
  end?: string;
}

/**
 * Per-bound resume state:
 * - `null`: the bound has not been fetched yet — start at its beginning.
 * - `QueryDocumentSnapshot`: resume after this document.
 * - `'done'`: the bound is exhausted — never query it again.
 */
export type GeoCursor = QueryDocumentSnapshot | 'done' | null;

/**
 * Returns the geohash bounds for `center` + `distanceKm`, or a single
 * unbounded entry when no geo filter applies.
 */
export function getGeoBounds(
  center: { latitude: number; longitude: number } | undefined,
  distanceKm: number | undefined
): GeoBound[] {
  if (!center || typeof distanceKm !== 'number') return [{}];
  return geohashQueryBounds([center.latitude, center.longitude], distanceKm * 1000).map(
    ([start, end]) => ({ start, end })
  );
}

/**
 * What to do with one bound in the next fetch round. Each bound keeps its own
 * cursor, so no bound is ever abandoned early — a bound that hit its page cap
 * is resumed rather than skipped, which is what previously dropped profiles
 * in dense areas once a single global cursor moved past the bound.
 */
export type BoundQueryPlan = { kind: 'skip' } | { kind: 'query'; resume: boolean };

export function planBoundQueries(
  bounds: GeoBound[],
  cursors: GeoCursor[] | null | undefined
): BoundQueryPlan[] {
  return bounds.map((_, i) => {
    const cursor = cursors?.[i] ?? null;
    if (cursor === 'done') return { kind: 'skip' };
    return { kind: 'query', resume: cursor !== null };
  });
}

export interface GeoQueryOptions {
  /** Bounds to paginate; index-aligned with `cursors`. */
  bounds: GeoBound[];
  /** Per-bound resume state; null/omitted fetches every bound from its start. */
  cursors?: GeoCursor[] | null;
  /** Constraints applied to every generated query (e.g. visibility filters). */
  constraints?: QueryConstraint[];
  /** Field holding the geohash used for ordering and bounds. */
  geohashField?: string;
  /** Per-bound document cap for this round. */
  limit: number;
}

/**
 * Builds one Firestore query per unexhausted bound, honoring each bound's own
 * cursor. Returns an array index-aligned with `bounds`, with `null` where the
 * bound is `'done'`.
 */
export function buildGeoQueries(base: Query, options: GeoQueryOptions): (Query | null)[] {
  const {
    bounds,
    cursors,
    constraints = [],
    geohashField = 'geohash',
    limit: queryLimit,
  } = options;
  const order = orderBy(geohashField);
  const plans = planBoundQueries(bounds, cursors);

  return bounds.map((bound, i) => {
    const plan = plans[i];
    if (plan.kind === 'skip') return null;
    const clauses: QueryConstraint[] = [...constraints, order];
    const cursor = cursors?.[i];
    if (cursor && cursor !== 'done') {
      clauses.push(startAfter(cursor));
    } else if (bound.start != null) {
      clauses.push(startAt(bound.start));
    }
    if (bound.end != null) clauses.push(endAt(bound.end));
    clauses.push(limit(queryLimit));
    return query(base, ...clauses);
  });
}

/**
 * Computes the next cursor per bound from this round's snapshots: the last
 * doc when the bound hit the page cap (more may remain), `'done'` otherwise.
 */
export function cursorsForSnaps(snaps: (QuerySnapshot | null)[], queryLimit: number): GeoCursor[] {
  return snaps.map(snap => {
    if (!snap || snap.docs.length < queryLimit) return 'done';
    return snap.docs[snap.docs.length - 1];
  });
}

export interface GeoPage {
  /** Merged, deduplicated, geohash-ordered docs from all bounds this round. */
  docs: QueryDocumentSnapshot[];
  /** Resume state for the next round, index-aligned with `bounds`. */
  cursors: GeoCursor[];
  /** True while at least one bound may still have unfetched docs. */
  hasMore: boolean;
}

/**
 * Fetches one round of a geohash-paginated collection: up to `limit` docs per
 * unexhausted bound, in parallel. Call repeatedly with the returned `cursors`
 * until `hasMore` is false to exhaust the whole area.
 */
export async function fetchGeoPage(base: Query, options: GeoQueryOptions): Promise<GeoPage> {
  const queries = buildGeoQueries(base, options);
  const snaps = await Promise.all(queries.map(q => (q ? getDocs(q) : Promise.resolve(null))));
  const cursors = cursorsForSnaps(snaps, options.limit);
  const docs = mergeGeoQueryDocs(
    snaps.filter((s): s is QuerySnapshot => s !== null),
    options.geohashField
  );
  return { docs, cursors, hasMore: cursors.some(c => c !== 'done') };
}

/**
 * Merges snapshots from multiple bound queries into one deduplicated,
 * geohash-ordered doc list (ties broken by document id). Dedupe is required
 * because `startAt`/`endAt` are inclusive: a doc exactly on a shared bound
 * edge can appear in two bound queries.
 */
export function mergeGeoQueryDocs(
  snaps: QuerySnapshot[],
  geohashField = 'geohash'
): QueryDocumentSnapshot[] {
  const seen = new Set<string>();
  return snaps
    .flatMap(s => s.docs)
    .filter(d => {
      if (seen.has(d.id)) return false;
      seen.add(d.id);
      return true;
    })
    .sort((a, b) => {
      const aHash = (a.data()[geohashField] as string) ?? '';
      const bHash = (b.data()[geohashField] as string) ?? '';
      const hashCmp = aHash.localeCompare(bHash);
      return hashCmp === 0 ? a.id.localeCompare(b.id) : hashCmp;
    });
}
